import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "hidden", "listbox", "hint"]
  static values = {
    url: String,
    debounceMs: { type: Number, default: 250 },
    minLength: { type: Number, default: 2 }
  }

  connect() {
    this.results = []
    this.activeIndex = -1
    this.debounceTimer = null
    this.abortController = null
    this.boundClickOutside = this.clickOutside.bind(this)
    document.addEventListener("click", this.boundClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.boundClickOutside)
    this.abortFetch()
    window.clearTimeout(this.debounceTimer)
  }

  onInput() {
    window.clearTimeout(this.debounceTimer)
    this.debounceTimer = window.setTimeout(() => this.fetchResults(), this.debounceMsValue)
  }

  onFocus() {
    if (this.inputTarget.value.trim().length >= this.minLengthValue) {
      this.fetchResults()
    } else {
      this.fetchResults(true)
    }
  }

  onKeydown(event) {
    if (!this.listboxTarget.hidden && this.results.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault()
        this.activeIndex = Math.min(this.activeIndex + 1, this.results.length - 1)
        this.highlightActive()
        return
      }
      if (event.key === "ArrowUp") {
        event.preventDefault()
        this.activeIndex = Math.max(this.activeIndex - 1, 0)
        this.highlightActive()
        return
      }
      if (event.key === "Enter" && this.activeIndex >= 0) {
        event.preventDefault()
        this.selectResult(this.results[this.activeIndex])
        return
      }
    }

    if (event.key === "Escape") {
      this.closeListbox()
    }
  }

  clickOutside(event) {
    if (!this.element.contains(event.target)) this.closeListbox()
  }

  async fetchResults(recent = false) {
    const query = this.inputTarget.value.trim()
    if (!recent && query.length < this.minLengthValue) {
      this.closeListbox()
      return
    }

    this.abortFetch()
    this.abortController = new AbortController()

    try {
      const url = new URL(this.urlValue, window.location.origin)
      if (query) url.searchParams.set("q", query)

      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal: this.abortController.signal
      })

      if (!response.ok) throw new Error("fetch failed")

      this.results = await response.json()
      this.activeIndex = -1
      this.renderResults()
    } catch (error) {
      if (error.name === "AbortError") return
      this.closeListbox()
    }
  }

  renderResults() {
    this.listboxTarget.innerHTML = ""

    if (this.results.length === 0) {
      const empty = document.createElement("li")
      empty.className = "px-3 py-2 text-xs text-muted"
      empty.textContent = this.inputTarget.value.trim().length >= this.minLengthValue
        ? this.hintTarget.dataset.emptyLabel || "No listings found."
        : this.hintTarget.textContent
      this.listboxTarget.appendChild(empty)
      this.openListbox()
      return
    }

    this.results.forEach((result, index) => {
      const item = document.createElement("li")
      item.setAttribute("role", "option")
      item.dataset.index = index

      const button = document.createElement("button")
      button.type = "button"
      button.className = "w-full rounded-lg px-3 py-2 text-left hover:bg-bg"
      button.dataset.action = "rental-listing-picker#choose"
      button.dataset.id = result.id
      button.dataset.label = result.label

      const name = document.createElement("span")
      name.className = "block text-sm font-semibold text-ink"
      name.textContent = result.label

      const meta = document.createElement("span")
      meta.className = "block truncate text-xs text-muted"
      meta.textContent = result.meta || result.address || ""

      button.appendChild(name)
      if (meta.textContent) button.appendChild(meta)
      item.appendChild(button)
      this.listboxTarget.appendChild(item)
    })

    this.openListbox()
  }

  choose(event) {
    event.preventDefault()
    const button = event.currentTarget
    this.selectResult({
      id: button.dataset.id,
      label: button.dataset.label
    })
  }

  selectResult(result) {
    this.hiddenTarget.value = result.id
    this.inputTarget.value = result.label
    this.inputTarget.setCustomValidity("")
    this.closeListbox()
    this.element.dispatchEvent(new CustomEvent("rental-listing:selected", {
      bubbles: true,
      detail: { listingId: result.id, label: result.label }
    }))
  }

  highlightActive() {
    this.listboxTarget.querySelectorAll("button").forEach((button, index) => {
      button.classList.toggle("bg-bg", index === this.activeIndex)
    })
  }

  openListbox() {
    this.listboxTarget.hidden = false
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  closeListbox() {
    this.listboxTarget.hidden = true
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.activeIndex = -1
  }

  abortFetch() {
    this.abortController?.abort()
    this.abortController = null
  }
}
